/**
 * Appendix B Option A — production TextArea toolbar (web + native).
 * Icons only — no text labels on the bar. Web: dense 32px icons, undo/redo
 * in a history flyout, wrap at group boundaries. Native: seven menus share
 * the card width (no horizontal peek-scroll). B I U live in Format. Mark
 * order follows Word/Teams.
 */
import ALargeSmall from 'lucide-react-native/dist/esm/icons/a-large-small.js';
import AlignCenter from 'lucide-react-native/dist/esm/icons/align-center.js';
import AlignJustify from 'lucide-react-native/dist/esm/icons/align-justify.js';
import AlignLeft from 'lucide-react-native/dist/esm/icons/align-left.js';
import AlignRight from 'lucide-react-native/dist/esm/icons/align-right.js';
import BetweenHorizontalEnd from 'lucide-react-native/dist/esm/icons/between-horizontal-end.js';
import BetweenHorizontalStart from 'lucide-react-native/dist/esm/icons/between-horizontal-start.js';
import BetweenVerticalEnd from 'lucide-react-native/dist/esm/icons/between-vertical-end.js';
import BetweenVerticalStart from 'lucide-react-native/dist/esm/icons/between-vertical-start.js';
import Bold from 'lucide-react-native/dist/esm/icons/bold.js';
import ChevronDown from 'lucide-react-native/dist/esm/icons/chevron-down.js';
import ChevronUp from 'lucide-react-native/dist/esm/icons/chevron-up.js';
import Code from 'lucide-react-native/dist/esm/icons/code.js';
import Eraser from 'lucide-react-native/dist/esm/icons/eraser.js';
import Heading1 from 'lucide-react-native/dist/esm/icons/heading-1.js';
import Heading2 from 'lucide-react-native/dist/esm/icons/heading-2.js';
import Heading3 from 'lucide-react-native/dist/esm/icons/heading-3.js';
import Heading from 'lucide-react-native/dist/esm/icons/heading.js';
import ImageIcon from 'lucide-react-native/dist/esm/icons/image.js';
import IndentDecrease from 'lucide-react-native/dist/esm/icons/indent-decrease.js';
import IndentIncrease from 'lucide-react-native/dist/esm/icons/indent-increase.js';
import Italic from 'lucide-react-native/dist/esm/icons/italic.js';
import Link2 from 'lucide-react-native/dist/esm/icons/link-2.js';
import ListOrdered from 'lucide-react-native/dist/esm/icons/list-ordered.js';
import List from 'lucide-react-native/dist/esm/icons/list.js';
import Palette from 'lucide-react-native/dist/esm/icons/palette.js';
import Pilcrow from 'lucide-react-native/dist/esm/icons/pilcrow.js';
import Plus from 'lucide-react-native/dist/esm/icons/plus.js';
import Redo2 from 'lucide-react-native/dist/esm/icons/redo-2.js';
import SquareCode from 'lucide-react-native/dist/esm/icons/square-code.js';
import Strikethrough from 'lucide-react-native/dist/esm/icons/strikethrough.js';
import Subscript from 'lucide-react-native/dist/esm/icons/subscript.js';
import Superscript from 'lucide-react-native/dist/esm/icons/superscript.js';
import Table2 from 'lucide-react-native/dist/esm/icons/table-2.js';
import TextQuote from 'lucide-react-native/dist/esm/icons/text-quote.js';
import Underline from 'lucide-react-native/dist/esm/icons/underline.js';
import Undo2 from 'lucide-react-native/dist/esm/icons/undo-2.js';
import React, { useCallback, useEffect, useState } from 'react';
import { Platform, Pressable, StyleSheet, Text, View } from 'react-native';

import { colors, fontSize, fontWeight } from '../../theme';

import type { LucideIcon } from 'lucide-react-native';

export const TEXT_AREA_FONT_SIZE_PRESETS = [
  '12px',
  '14px',
  '16px',
  '18px',
  '24px',
] as const;

export const TEXT_AREA_TEXT_COLORS = [
  { id: 'black', label: 'Black', value: '#111827' },
  { id: 'red', label: 'Red', value: '#DC2626' },
  { id: 'blue', label: 'Blue', value: '#2563EB' },
  { id: 'green', label: 'Green', value: '#059669' },
  { id: 'orange', label: 'Orange', value: '#D97706' },
] as const;

export const TEXT_AREA_HIGHLIGHT_COLORS = [
  { id: 'yellow', label: 'Yellow', value: '#FEF08A' },
  { id: 'green', label: 'Green', value: '#BBF7D0' },
  { id: 'blue', label: 'Blue', value: '#BFDBFE' },
] as const;

export type TextAreaFontSizePreset =
  (typeof TEXT_AREA_FONT_SIZE_PRESETS)[number];

const HEADING_LEVEL_ICONS: Record<1 | 2 | 3, LucideIcon> = {
  1: Heading1,
  2: Heading2,
  3: Heading3,
};

export type TextAreaToolbarAlign = 'left' | 'center' | 'right' | 'justify';

export type TextAreaToolbarState = {
  canUndo: boolean;
  canRedo: boolean;
  isBoldActive: boolean;
  isItalicActive: boolean;
  isUnderlineActive: boolean;
  isStrikeActive: boolean;
  isCodeActive: boolean;
  isBulletListActive: boolean;
  isOrderedListActive: boolean;
  isLinkActive: boolean;
  canSetLink: boolean;
  isSubscriptActive: boolean;
  isSuperscriptActive: boolean;
  isTableActive?: boolean;
  /** The caret is inside a `<blockquote>` (Quote, message set; #1530 Teams parity). */
  isBlockquoteActive: boolean;
  /** The caret is inside a `<pre><code>` (Code snippet, message set). */
  isCodeBlockActive: boolean;
  activeFontSize?: string;
  activeColor?: string;
  activeHighlight?: string;
  activeTextAlign?: TextAreaToolbarAlign;
  headingLevel?: number;
};

export type TextAreaToolbarActions = {
  onUndo: () => void;
  onRedo: () => void;
  onToggleHeadingPicker: () => void;
  onSetParagraph: () => void;
  onToggleHeading: (level: 1 | 2 | 3) => void;
  onToggleBulletList: () => void;
  onToggleOrderedList: () => void;
  onIndent: () => void;
  onOutdent: () => void;
  onBold: () => void;
  onItalic: () => void;
  onStrike: () => void;
  onCode: () => void;
  onUnderline: () => void;
  onClearFormatting: () => void;
  onLink: () => void;
  onSuperscript: () => void;
  onSubscript: () => void;
  onSetFontSize: (size: TextAreaFontSizePreset) => void;
  /** "Default size": clears the font-size mark so the text takes the body size. */
  onUnsetFontSize: () => void;
  onAlign: (align: TextAreaToolbarAlign) => void;
  onInsertImage: () => void;
  onInsertTable: () => void;
  onAddTableRow: () => void;
  onDeleteTableRow: () => void;
  onAddTableColumn: () => void;
  onDeleteTableColumn: () => void;
  onSetColor: (color: string) => void;
  onUnsetColor: () => void;
  onToggleHighlight: (color: string) => void;
  /** Quote (#1530 Teams parity): wraps the block in a `<blockquote>`. */
  onToggleBlockquote: () => void;
  /** Code snippet (#1530 Teams parity): turns the block into a `<pre><code>`. */
  onToggleCodeBlock: () => void;
};

/**
 * Which buttons the `full` row (and the `compact` flyouts) offer (#1530
 * Teams-parity follow-up, owner 2026-10-05). `full` is the request-field
 * document set, unchanged. `message` is the Teams messaging set: B I U S,
 * colour + highlight, font size, paragraph/heading, lists + indent, Quote,
 * Code snippet, link, table, image, clear. It drops superscript, subscript,
 * the four alignments, inline code and the undo/redo buttons (keyboard
 * history still works). A SELECTION of the same buttons, not a new editor:
 * every control is the same `IconButton` wired to the same action.
 */
export type TextAreaToolbarSet = 'full' | 'message';

/**
 * `expandable` (#1530, owner walk 2026-10-01): the `reduced` row with a
 * trailing "More formatting" toggle; expanded, the `full` row replaces it
 * (the toggle flips to "Fewer formatting options"). No third button set: the
 * two existing rows swap. The choice is remembered for the session. Which
 * buttons the expanded row carries is `set` (`TextAreaToolbarSet`); the
 * collapsed row is the same whichever set is chosen.
 */
export type TextAreaToolbarLayout =
  | 'full'
  | 'compact'
  | 'reduced'
  | 'expandable';

export type TextAreaToolbarProps = {
  testIdPrefix: string;
  state: TextAreaToolbarState;
  actions: TextAreaToolbarActions;
  showHeadingPicker: boolean;
  imageBusy?: boolean;
  showJustify?: boolean;
  layout?: TextAreaToolbarLayout;
  /** Which button set the `full` row and `compact` flyouts offer. Default `full`. */
  set?: TextAreaToolbarSet;
  /**
   * The host's read-only switch (the composer passes `!submitting`). A
   * flyout open when it flips to `false` closes: the row under it is no
   * longer taking input, so a swatch that could not apply must not stay up.
   */
  editable?: boolean;
  /** Rendered after the last button of the `reduced` and `full` rows. */
  trailing?: React.ReactNode;
};

type FlyoutPanel =
  | 'history'
  | 'list'
  | 'format'
  | 'color'
  | 'insert'
  | 'align'
  | null;

function colorsMatch(left?: string, right?: string): boolean {
  return Boolean(left && right && left.toLowerCase() === right.toLowerCase());
}

type ColorSwatchButtonProps = {
  testID: string;
  accessibilityLabel: string;
  swatch: string;
  active?: boolean;
  onPress: () => void;
};

function ToolbarPressable(
  props: React.ComponentProps<typeof Pressable>,
): React.JSX.Element {
  const { onPress, ...rest } = props;
  return (
    <Pressable
      {...rest}
      onPress={onPress}
      {...({
        onMouseDown: (event: { preventDefault: () => void }) => {
          event.preventDefault();
        },
      } as object)}
    />
  );
}

function ColorSwatchButton({
  testID,
  accessibilityLabel,
  swatch,
  active = false,
  onPress,
}: ColorSwatchButtonProps): React.JSX.Element {
  return (
    <ToolbarPressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected: active }}
      onPress={onPress}
      style={[styles.swatchButton, active && styles.buttonActive]}
    >
      <View style={[styles.swatch, { backgroundColor: swatch }]} />
    </ToolbarPressable>
  );
}

type IconButtonProps = {
  testID: string;
  accessibilityLabel: string;
  active?: boolean;
  disabled?: boolean;
  onPress: () => void;
  icon: LucideIcon;
  trailingChevron?: boolean;
  compact?: boolean;
};

type FontSizePresetButtonProps = {
  testID: string;
  size: TextAreaFontSizePreset;
  active?: boolean;
  onPress: () => void;
};

function FontSizePresetButton({
  testID,
  size,
  active = false,
  onPress,
}: FontSizePresetButtonProps): React.JSX.Element {
  const label = size.replace('px', '');
  const textColor = active ? colors.primary : colors.textSecondary;

  return (
    <ToolbarPressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={`Font size ${size}`}
      accessibilityState={{ selected: active }}
      onPress={onPress}
      style={[styles.sizeButton, active && styles.buttonActive]}
    >
      <Text style={[styles.sizeButtonText, { color: textColor }]}>{label}</Text>
    </ToolbarPressable>
  );
}

function IconButton({
  testID,
  accessibilityLabel,
  active = false,
  disabled = false,
  onPress,
  icon: Icon,
  trailingChevron = false,
  compact = false,
}: IconButtonProps): React.JSX.Element {
  const iconColor = active ? colors.primary : colors.textSecondary;

  return (
    <ToolbarPressable
      testID={testID}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel}
      accessibilityState={{ selected: active, disabled }}
      disabled={disabled}
      onPress={onPress}
      style={[
        styles.button,
        trailingChevron && styles.buttonChevron,
        compact && !trailingChevron && styles.buttonCompact,
        compact && trailingChevron && styles.buttonCompactChevron,
        active && styles.buttonActive,
        disabled && styles.buttonDisabled,
      ]}
    >
      <Icon size={compact ? 16 : 15} color={iconColor} strokeWidth={1.75} />
      {trailingChevron ? (
        <ChevronDown size={7} color={iconColor} strokeWidth={1.75} />
      ) : null}
    </ToolbarPressable>
  );
}

function ToolbarDivider(): React.JSX.Element {
  return <View style={styles.divider} accessibilityElementsHidden />;
}

function ToolbarGroup({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element {
  return <View style={styles.group}>{children}</View>;
}

function tid(prefix: string, id: string): string {
  return id === 'toolbar' ? `${prefix}-toolbar` : `${prefix}-${id}`;
}

function resolveListIcon(state: TextAreaToolbarState): LucideIcon {
  if (state.isOrderedListActive) {
    return ListOrdered;
  }
  return List;
}

function resolveFormatIcon(state: TextAreaToolbarState): LucideIcon {
  if (state.isBoldActive) {
    return Bold;
  }
  if (state.isItalicActive) {
    return Italic;
  }
  if (state.isUnderlineActive) {
    return Underline;
  }
  if (state.isStrikeActive) {
    return Strikethrough;
  }
  if (state.isCodeActive) {
    return Code;
  }
  if (state.activeFontSize) {
    return ALargeSmall;
  }
  return Bold;
}

function resolveAlignIcon(state: TextAreaToolbarState): LucideIcon {
  switch (state.activeTextAlign) {
    case 'center':
      return AlignCenter;
    case 'right':
      return AlignRight;
    case 'justify':
      return AlignJustify;
    default:
      return AlignLeft;
  }
}

function HistoryFlyout({
  testIdPrefix,
  state,
  actions,
  onDone,
}: Pick<TextAreaToolbarProps, 'testIdPrefix' | 'state' | 'actions'> & {
  onDone?: () => void;
}): React.JSX.Element {
  return (
    <View style={styles.flyoutRow} testID={tid(testIdPrefix, 'history-picker')}>
      <IconButton
        testID={tid(testIdPrefix, 'undo')}
        accessibilityLabel="Undo"
        disabled={!state.canUndo}
        onPress={() => {
          actions.onUndo();
          onDone?.();
        }}
        icon={Undo2}
      />
      <IconButton
        testID={tid(testIdPrefix, 'redo')}
        accessibilityLabel="Redo"
        disabled={!state.canRedo}
        onPress={() => {
          actions.onRedo();
          onDone?.();
        }}
        icon={Redo2}
      />
    </View>
  );
}

function HeadingFlyout({
  testIdPrefix,
  state,
  actions,
}: Pick<
  TextAreaToolbarProps,
  'testIdPrefix' | 'state' | 'actions'
>): React.JSX.Element {
  return (
    <View
      style={[styles.flyoutRow, styles.flyoutHeading]}
      testID={tid(testIdPrefix, 'heading-picker')}
    >
      <IconButton
        testID={tid(testIdPrefix, 'paragraph')}
        accessibilityLabel="Paragraph"
        active={!state.headingLevel}
        onPress={actions.onSetParagraph}
        icon={Pilcrow}
      />
      {([1, 2, 3] as const).map(level => (
        <IconButton
          key={level}
          testID={tid(testIdPrefix, `h${level}`)}
          accessibilityLabel={`Heading ${level}`}
          active={state.headingLevel === level}
          onPress={() => actions.onToggleHeading(level)}
          icon={HEADING_LEVEL_ICONS[level]}
        />
      ))}
    </View>
  );
}

/** Text colour swatches + clear, then the highlight swatches. One rendering,
 *  shared by the compact `color-menu` and the message row's `color-menu`. */
function ColorFlyout({
  testIdPrefix,
  state,
  actions,
}: Pick<
  TextAreaToolbarProps,
  'testIdPrefix' | 'state' | 'actions'
>): React.JSX.Element {
  return (
    <View testID={tid(testIdPrefix, 'color-picker')}>
      <View style={styles.flyoutRow} accessibilityLabel="Text colors">
        {TEXT_AREA_TEXT_COLORS.map(color => (
          <ColorSwatchButton
            key={`text-${color.id}`}
            testID={tid(testIdPrefix, `color-${color.id}`)}
            accessibilityLabel={`Text color ${color.label}`}
            swatch={color.value}
            active={colorsMatch(state.activeColor, color.value)}
            onPress={() => actions.onSetColor(color.value)}
          />
        ))}
        <IconButton
          testID={tid(testIdPrefix, 'color-clear')}
          accessibilityLabel="Clear text color"
          onPress={() => actions.onUnsetColor()}
          icon={Eraser}
        />
      </View>
      <Text style={styles.flyoutLabel}>Highlight</Text>
      <View style={styles.flyoutRow} accessibilityLabel="Highlight colors">
        {TEXT_AREA_HIGHLIGHT_COLORS.map(color => (
          <ColorSwatchButton
            key={`highlight-${color.id}`}
            testID={tid(testIdPrefix, `highlight-${color.id}`)}
            accessibilityLabel={`${color.label} highlight`}
            swatch={color.value}
            active={colorsMatch(state.activeHighlight, color.value)}
            onPress={() => actions.onToggleHighlight(color.value)}
          />
        ))}
      </View>
    </View>
  );
}

/** The `TEXT_AREA_FONT_SIZE_PRESETS` row. Shared by the compact Format
 *  flyout (second row) and the message row's `font-size-menu`. */
function FontSizeFlyout({
  testIdPrefix,
  state,
  actions,
}: Pick<
  TextAreaToolbarProps,
  'testIdPrefix' | 'state' | 'actions'
>): React.JSX.Element {
  return (
    <View style={styles.flyoutRow} accessibilityLabel="Font size">
      <ToolbarPressable
        testID={tid(testIdPrefix, 'font-size-default')}
        accessibilityRole="button"
        accessibilityLabel="Default size"
        accessibilityState={{ selected: !state.activeFontSize }}
        onPress={() => actions.onUnsetFontSize()}
        style={[
          styles.sizeButton,
          !state.activeFontSize && styles.buttonActive,
        ]}
      >
        <Text
          style={[
            styles.sizeButtonText,
            {
              color: state.activeFontSize
                ? colors.textSecondary
                : colors.primary,
            },
          ]}
        >
          Default
        </Text>
      </ToolbarPressable>
      {TEXT_AREA_FONT_SIZE_PRESETS.map(size => (
        <FontSizePresetButton
          key={size}
          testID={tid(testIdPrefix, `font-size-${size}`)}
          size={size}
          active={state.activeFontSize === size}
          onPress={() => actions.onSetFontSize(size)}
        />
      ))}
    </View>
  );
}

/**
 * The Teams message set on one wrapping row (#1530 Teams parity, owner
 * 2026-10-05). Colour and font size open the SAME flyouts the compact
 * toolbar uses, under the row; the heading picker is the host's (rendered by
 * `ExpandableToolbar` / `TextAreaToolbar`), so opening a local flyout closes
 * it and vice versa, the way `FullToolbarRow` already treats history.
 */
function MessageToolbarRow({
  testIdPrefix,
  state,
  actions,
  showHeadingPicker,
  imageBusy,
  editable,
  trailing,
}: TextAreaToolbarProps): React.JSX.Element {
  const [openPanel, setOpenPanel] = useState<'color' | 'font-size' | null>(
    null,
  );
  const headingActive = Boolean(state.headingLevel);

  useEffect(() => {
    if (showHeadingPicker) {
      setOpenPanel(null);
    }
  }, [showHeadingPicker]);

  // Read-only (sending): nothing under the row takes input, so a flyout
  // left open would offer swatches that cannot apply.
  useEffect(() => {
    if (editable === false) {
      setOpenPanel(null);
    }
  }, [editable]);

  // A flyout is a menu: a choice closes it (owner review, 2026-10-05). The
  // compact toolbar keeps its flyouts open, which fits a bar that IS the
  // menus; here the row is the toolbar and the flyout is a one-shot pick.
  const closeAfter = useCallback(
    <A extends unknown[]>(action: (...args: A) => void) =>
      (...args: A) => {
        action(...args);
        setOpenPanel(null);
      },
    [],
  );
  const flyoutActions: TextAreaToolbarActions = {
    ...actions,
    onSetColor: closeAfter(actions.onSetColor),
    onUnsetColor: closeAfter(actions.onUnsetColor),
    onToggleHighlight: closeAfter(actions.onToggleHighlight),
    onSetFontSize: closeAfter(actions.onSetFontSize),
    onUnsetFontSize: closeAfter(actions.onUnsetFontSize),
  };

  const togglePanel = (panel: 'color' | 'font-size') => {
    if (showHeadingPicker) {
      actions.onToggleHeadingPicker();
    }
    setOpenPanel(current => (current === panel ? null : panel));
  };

  const onHeadingPress = () => {
    setOpenPanel(null);
    actions.onToggleHeadingPicker();
  };

  return (
    <>
      <View
        style={styles.rowFull}
        accessibilityLabel="Rich text formatting"
        accessibilityRole="toolbar"
      >
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'bold')}
            accessibilityLabel="Bold"
            active={state.isBoldActive}
            onPress={actions.onBold}
            icon={Bold}
          />
          <IconButton
            testID={tid(testIdPrefix, 'italic')}
            accessibilityLabel="Italic"
            active={state.isItalicActive}
            onPress={actions.onItalic}
            icon={Italic}
          />
          <IconButton
            testID={tid(testIdPrefix, 'underline')}
            accessibilityLabel="Underline"
            active={state.isUnderlineActive}
            onPress={actions.onUnderline}
            icon={Underline}
          />
          <IconButton
            testID={tid(testIdPrefix, 'strike')}
            accessibilityLabel="Strikethrough"
            active={state.isStrikeActive}
            onPress={actions.onStrike}
            icon={Strikethrough}
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'color-menu')}
            accessibilityLabel="Text color and highlight"
            active={openPanel === 'color'}
            onPress={() => togglePanel('color')}
            icon={Palette}
            trailingChevron
          />
          <IconButton
            testID={tid(testIdPrefix, 'font-size-menu')}
            accessibilityLabel="Font size"
            active={openPanel === 'font-size'}
            onPress={() => togglePanel('font-size')}
            icon={ALargeSmall}
            trailingChevron
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'heading')}
            accessibilityLabel="Heading style"
            active={headingActive}
            onPress={onHeadingPress}
            icon={Heading}
            trailingChevron
          />
          <IconButton
            testID={tid(testIdPrefix, 'bullet')}
            accessibilityLabel="Bulleted list"
            active={state.isBulletListActive}
            onPress={actions.onToggleBulletList}
            icon={List}
          />
          <IconButton
            testID={tid(testIdPrefix, 'ordered')}
            accessibilityLabel="Numbered list"
            active={state.isOrderedListActive}
            onPress={actions.onToggleOrderedList}
            icon={ListOrdered}
          />
          <IconButton
            testID={tid(testIdPrefix, 'outdent')}
            accessibilityLabel="Decrease indent"
            onPress={actions.onOutdent}
            icon={IndentDecrease}
          />
          <IconButton
            testID={tid(testIdPrefix, 'indent')}
            accessibilityLabel="Increase indent"
            onPress={actions.onIndent}
            icon={IndentIncrease}
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'blockquote')}
            accessibilityLabel="Quote"
            active={state.isBlockquoteActive}
            onPress={actions.onToggleBlockquote}
            icon={TextQuote}
          />
          <IconButton
            testID={tid(testIdPrefix, 'code-block')}
            accessibilityLabel="Code snippet"
            active={state.isCodeBlockActive}
            onPress={actions.onToggleCodeBlock}
            icon={SquareCode}
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'link')}
            accessibilityLabel="Insert or remove link"
            active={state.isLinkActive}
            disabled={!state.canSetLink && !state.isLinkActive}
            onPress={actions.onLink}
            icon={Link2}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-insert')}
            accessibilityLabel="Insert 3 by 3 table"
            active={state.isTableActive}
            onPress={actions.onInsertTable}
            icon={Table2}
          />
          <IconButton
            testID={tid(testIdPrefix, 'image')}
            accessibilityLabel="Insert image"
            disabled={imageBusy}
            onPress={actions.onInsertImage}
            icon={ImageIcon}
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'clear-formatting')}
            accessibilityLabel="Clear formatting"
            onPress={actions.onClearFormatting}
            icon={Eraser}
          />
        </ToolbarGroup>
        {trailing}
      </View>
      {openPanel === 'color' ? (
        <ColorFlyout
          testIdPrefix={testIdPrefix}
          state={state}
          actions={flyoutActions}
        />
      ) : null}
      {openPanel === 'font-size' ? (
        <FontSizeFlyout
          testIdPrefix={testIdPrefix}
          state={state}
          actions={flyoutActions}
        />
      ) : null}
    </>
  );
}

function FullToolbarRow(props: TextAreaToolbarProps): React.JSX.Element {
  if (props.set === 'message') {
    return <MessageToolbarRow {...props} />;
  }
  return <DocumentToolbarRow {...props} />;
}

/** The request-field document set: today's `full` row, byte for byte. */
function DocumentToolbarRow({
  testIdPrefix,
  state,
  actions,
  showHeadingPicker,
  showJustify,
  imageBusy,
  trailing,
}: TextAreaToolbarProps): React.JSX.Element {
  const [showHistoryPicker, setShowHistoryPicker] = useState(false);
  const headingActive = Boolean(state.headingLevel);

  useEffect(() => {
    if (showHeadingPicker) {
      setShowHistoryPicker(false);
    }
  }, [showHeadingPicker]);

  const onHistoryPress = () => {
    if (showHeadingPicker) {
      actions.onToggleHeadingPicker();
    }
    setShowHistoryPicker(open => !open);
  };

  return (
    <>
      <View
        style={styles.rowFull}
        accessibilityLabel="Rich text formatting"
        accessibilityRole="toolbar"
      >
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'history-menu')}
            accessibilityLabel="Undo and redo"
            active={showHistoryPicker}
            onPress={onHistoryPress}
            icon={Undo2}
            trailingChevron
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'heading')}
            accessibilityLabel="Heading style"
            active={headingActive}
            onPress={actions.onToggleHeadingPicker}
            icon={Heading}
            trailingChevron
          />
          <IconButton
            testID={tid(testIdPrefix, 'ordered')}
            accessibilityLabel="Numbered list"
            active={state.isOrderedListActive}
            onPress={actions.onToggleOrderedList}
            icon={ListOrdered}
          />
          <IconButton
            testID={tid(testIdPrefix, 'bullet')}
            accessibilityLabel="Bulleted list"
            active={state.isBulletListActive}
            onPress={actions.onToggleBulletList}
            icon={List}
          />
          <IconButton
            testID={tid(testIdPrefix, 'outdent')}
            accessibilityLabel="Decrease indent"
            onPress={actions.onOutdent}
            icon={IndentDecrease}
          />
          <IconButton
            testID={tid(testIdPrefix, 'indent')}
            accessibilityLabel="Increase indent"
            onPress={actions.onIndent}
            icon={IndentIncrease}
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'bold')}
            accessibilityLabel="Bold"
            active={state.isBoldActive}
            onPress={actions.onBold}
            icon={Bold}
          />
          <IconButton
            testID={tid(testIdPrefix, 'italic')}
            accessibilityLabel="Italic"
            active={state.isItalicActive}
            onPress={actions.onItalic}
            icon={Italic}
          />
          <IconButton
            testID={tid(testIdPrefix, 'underline')}
            accessibilityLabel="Underline"
            active={state.isUnderlineActive}
            onPress={actions.onUnderline}
            icon={Underline}
          />
          <IconButton
            testID={tid(testIdPrefix, 'strike')}
            accessibilityLabel="Strikethrough"
            active={state.isStrikeActive}
            onPress={actions.onStrike}
            icon={Strikethrough}
          />
          <IconButton
            testID={tid(testIdPrefix, 'code')}
            accessibilityLabel="Inline code"
            active={state.isCodeActive}
            onPress={actions.onCode}
            icon={Code}
          />
          <IconButton
            testID={tid(testIdPrefix, 'clear-formatting')}
            accessibilityLabel="Clear formatting"
            onPress={actions.onClearFormatting}
            icon={Eraser}
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'link')}
            accessibilityLabel="Insert or remove link"
            active={state.isLinkActive}
            disabled={!state.canSetLink && !state.isLinkActive}
            onPress={actions.onLink}
            icon={Link2}
          />
        </ToolbarGroup>
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'sup')}
            accessibilityLabel="Superscript"
            active={state.isSuperscriptActive}
            onPress={actions.onSuperscript}
            icon={Superscript}
          />
          <IconButton
            testID={tid(testIdPrefix, 'sub')}
            accessibilityLabel="Subscript"
            active={state.isSubscriptActive}
            onPress={actions.onSubscript}
            icon={Subscript}
          />
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'align-left')}
            accessibilityLabel="Align left"
            active={state.activeTextAlign === 'left' || !state.activeTextAlign}
            onPress={() => actions.onAlign('left')}
            icon={AlignLeft}
          />
          <IconButton
            testID={tid(testIdPrefix, 'align-center')}
            accessibilityLabel="Align center"
            active={state.activeTextAlign === 'center'}
            onPress={() => actions.onAlign('center')}
            icon={AlignCenter}
          />
          <IconButton
            testID={tid(testIdPrefix, 'align-right')}
            accessibilityLabel="Align right"
            active={state.activeTextAlign === 'right'}
            onPress={() => actions.onAlign('right')}
            icon={AlignRight}
          />
          {showJustify !== false ? (
            <IconButton
              testID={tid(testIdPrefix, 'align-justify')}
              accessibilityLabel="Justify"
              active={state.activeTextAlign === 'justify'}
              onPress={() => actions.onAlign('justify')}
              icon={AlignJustify}
            />
          ) : null}
        </ToolbarGroup>
        <ToolbarDivider />
        <ToolbarGroup>
          <IconButton
            testID={tid(testIdPrefix, 'image')}
            accessibilityLabel="Insert image"
            disabled={imageBusy}
            onPress={actions.onInsertImage}
            icon={ImageIcon}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-insert')}
            accessibilityLabel="Insert 3 by 3 table"
            active={state.isTableActive}
            onPress={actions.onInsertTable}
            icon={Table2}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-add-row')}
            accessibilityLabel="Add table row"
            disabled={!state.isTableActive}
            onPress={actions.onAddTableRow}
            icon={BetweenHorizontalStart}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-del-row')}
            accessibilityLabel="Delete table row"
            disabled={!state.isTableActive}
            onPress={actions.onDeleteTableRow}
            icon={BetweenHorizontalEnd}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-add-col')}
            accessibilityLabel="Add table column"
            disabled={!state.isTableActive}
            onPress={actions.onAddTableColumn}
            icon={BetweenVerticalStart}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-del-col')}
            accessibilityLabel="Delete table column"
            disabled={!state.isTableActive}
            onPress={actions.onDeleteTableColumn}
            icon={BetweenVerticalEnd}
          />
        </ToolbarGroup>
        {trailing}
      </View>
      {showHistoryPicker ? (
        <HistoryFlyout
          testIdPrefix={testIdPrefix}
          state={state}
          actions={actions}
          onDone={() => setShowHistoryPicker(false)}
        />
      ) : null}
    </>
  );
}

function CompactToolbar({
  testIdPrefix,
  state,
  actions,
  showHeadingPicker,
  showJustify,
  imageBusy,
  set = 'full',
}: TextAreaToolbarProps): React.JSX.Element {
  const [openPanel, setOpenPanel] = useState<FlyoutPanel>(null);
  // The message set (#1530 Teams parity): no Alignment menu, and the Format
  // flyout trades sup / sub / inline code for Quote + Code snippet. Table
  // insert already lives in the Insert flyout, so nothing moves. The align
  // path is ABSENT in the message set, not guarded: `alignMenu` is null, so
  // no button can call `togglePanel('align')` and `openPanel` can never
  // hold it; the align picker below renders on `openPanel` alone.
  const isMessageSet = set === 'message';

  const closeHeadingPicker = useCallback(() => {
    if (showHeadingPicker) {
      actions.onToggleHeadingPicker();
    }
  }, [actions, showHeadingPicker]);

  const closePanels = useCallback(() => {
    setOpenPanel(null);
    closeHeadingPicker();
  }, [closeHeadingPicker]);

  const togglePanel = useCallback(
    (panel: FlyoutPanel) => {
      closeHeadingPicker();
      setOpenPanel(current => (current === panel ? null : panel));
    },
    [closeHeadingPicker],
  );

  const headingActive = showHeadingPicker;
  const historyActive = openPanel === 'history';
  const listActive = openPanel === 'list';
  const formatActive = openPanel === 'format';
  const colorActive = openPanel === 'color';
  const insertActive = openPanel === 'insert';
  const alignMenu = isMessageSet
    ? null
    : { active: openPanel === 'align', icon: resolveAlignIcon(state) };

  const onHeadingPress = () => {
    setOpenPanel(null);
    actions.onToggleHeadingPicker();
  };

  return (
    <View testID={tid(testIdPrefix, 'toolbar')}>
      <View
        testID={tid(testIdPrefix, 'toolbar-row')}
        accessibilityLabel="Rich text formatting"
        accessibilityRole="toolbar"
        style={styles.rowCompact}
      >
        {isMessageSet ? null : (
          /* No undo/redo in the message set (owner, 2026-10-05). */
          <IconButton
            compact
            testID={tid(testIdPrefix, 'history-menu')}
            accessibilityLabel="Undo and redo"
            active={historyActive}
            onPress={() => togglePanel('history')}
            icon={Undo2}
            trailingChevron
          />
        )}
        <IconButton
          compact
          testID={tid(testIdPrefix, 'heading')}
          accessibilityLabel="Heading style"
          active={headingActive}
          onPress={onHeadingPress}
          icon={Heading}
          trailingChevron
        />
        <IconButton
          compact
          testID={tid(testIdPrefix, 'list-menu')}
          accessibilityLabel="List and indent"
          active={listActive}
          onPress={() => togglePanel('list')}
          icon={resolveListIcon(state)}
          trailingChevron
        />
        <IconButton
          compact
          testID={tid(testIdPrefix, 'format-menu')}
          accessibilityLabel="Text formatting"
          active={formatActive}
          onPress={() => togglePanel('format')}
          icon={resolveFormatIcon(state)}
          trailingChevron
        />
        <IconButton
          compact
          testID={tid(testIdPrefix, 'color-menu')}
          accessibilityLabel="Text color and highlight"
          active={colorActive}
          onPress={() => togglePanel('color')}
          icon={Palette}
          trailingChevron
        />
        <IconButton
          compact
          testID={tid(testIdPrefix, 'insert-menu')}
          accessibilityLabel="Insert link, image, or table"
          active={insertActive}
          onPress={() => togglePanel('insert')}
          icon={Plus}
          trailingChevron
        />
        {alignMenu ? (
          <IconButton
            compact
            testID={tid(testIdPrefix, 'align-menu')}
            accessibilityLabel="Text alignment"
            active={alignMenu.active}
            onPress={() => togglePanel('align')}
            icon={alignMenu.icon}
            trailingChevron
          />
        ) : null}
      </View>

      {showHeadingPicker ? (
        <HeadingFlyout
          testIdPrefix={testIdPrefix}
          state={state}
          actions={{
            ...actions,
            onSetParagraph: () => {
              actions.onSetParagraph();
              closeHeadingPicker();
            },
            onToggleHeading: level => {
              actions.onToggleHeading(level);
              closeHeadingPicker();
            },
          }}
        />
      ) : null}

      {openPanel === 'history' ? (
        <HistoryFlyout
          testIdPrefix={testIdPrefix}
          state={state}
          actions={actions}
          onDone={closePanels}
        />
      ) : null}

      {openPanel === 'list' ? (
        <View
          style={styles.flyoutRow}
          testID={tid(testIdPrefix, 'list-picker')}
        >
          <IconButton
            testID={tid(testIdPrefix, 'bullet')}
            accessibilityLabel="Bulleted list"
            active={state.isBulletListActive}
            onPress={() => actions.onToggleBulletList()}
            icon={List}
          />
          <IconButton
            testID={tid(testIdPrefix, 'ordered')}
            accessibilityLabel="Numbered list"
            active={state.isOrderedListActive}
            onPress={() => actions.onToggleOrderedList()}
            icon={ListOrdered}
          />
          <IconButton
            testID={tid(testIdPrefix, 'outdent')}
            accessibilityLabel="Decrease indent"
            onPress={() => actions.onOutdent()}
            icon={IndentDecrease}
          />
          <IconButton
            testID={tid(testIdPrefix, 'indent')}
            accessibilityLabel="Increase indent"
            onPress={() => actions.onIndent()}
            icon={IndentIncrease}
          />
        </View>
      ) : null}

      {openPanel === 'format' ? (
        <View testID={tid(testIdPrefix, 'format-picker')}>
          <View style={styles.flyoutRow}>
            <IconButton
              testID={tid(testIdPrefix, 'bold')}
              accessibilityLabel="Bold"
              active={state.isBoldActive}
              onPress={() => actions.onBold()}
              icon={Bold}
            />
            <IconButton
              testID={tid(testIdPrefix, 'italic')}
              accessibilityLabel="Italic"
              active={state.isItalicActive}
              onPress={() => actions.onItalic()}
              icon={Italic}
            />
            <IconButton
              testID={tid(testIdPrefix, 'underline')}
              accessibilityLabel="Underline"
              active={state.isUnderlineActive}
              onPress={() => actions.onUnderline()}
              icon={Underline}
            />
            <IconButton
              testID={tid(testIdPrefix, 'strike')}
              accessibilityLabel="Strikethrough"
              active={state.isStrikeActive}
              onPress={() => actions.onStrike()}
              icon={Strikethrough}
            />
            {isMessageSet ? (
              <>
                <IconButton
                  testID={tid(testIdPrefix, 'blockquote')}
                  accessibilityLabel="Quote"
                  active={state.isBlockquoteActive}
                  onPress={() => actions.onToggleBlockquote()}
                  icon={TextQuote}
                />
                <IconButton
                  testID={tid(testIdPrefix, 'code-block')}
                  accessibilityLabel="Code snippet"
                  active={state.isCodeBlockActive}
                  onPress={() => actions.onToggleCodeBlock()}
                  icon={SquareCode}
                />
              </>
            ) : (
              <IconButton
                testID={tid(testIdPrefix, 'code')}
                accessibilityLabel="Inline code"
                active={state.isCodeActive}
                onPress={() => actions.onCode()}
                icon={Code}
              />
            )}
            <IconButton
              testID={tid(testIdPrefix, 'clear-formatting')}
              accessibilityLabel="Clear formatting"
              onPress={() => actions.onClearFormatting()}
              icon={Eraser}
            />
          </View>
          <FontSizeFlyout
            testIdPrefix={testIdPrefix}
            state={state}
            actions={actions}
          />
        </View>
      ) : null}

      {openPanel === 'color' ? (
        <ColorFlyout
          testIdPrefix={testIdPrefix}
          state={state}
          actions={actions}
        />
      ) : null}

      {openPanel === 'insert' ? (
        <View
          style={styles.flyoutRow}
          testID={tid(testIdPrefix, 'insert-picker')}
        >
          <IconButton
            testID={tid(testIdPrefix, 'link')}
            accessibilityLabel="Insert or remove link"
            active={state.isLinkActive}
            disabled={!state.canSetLink && !state.isLinkActive}
            onPress={() => {
              actions.onLink();
              closePanels();
            }}
            icon={Link2}
          />
          <IconButton
            testID={tid(testIdPrefix, 'image')}
            accessibilityLabel="Insert image"
            disabled={imageBusy}
            onPress={() => {
              actions.onInsertImage();
              closePanels();
            }}
            icon={ImageIcon}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-insert')}
            accessibilityLabel="Insert 3 by 3 table"
            active={state.isTableActive}
            onPress={() => {
              actions.onInsertTable();
              closePanels();
            }}
            icon={Table2}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-add-row')}
            accessibilityLabel="Add table row"
            disabled={!state.isTableActive}
            onPress={() => {
              actions.onAddTableRow();
              closePanels();
            }}
            icon={BetweenHorizontalStart}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-del-row')}
            accessibilityLabel="Delete table row"
            disabled={!state.isTableActive}
            onPress={() => {
              actions.onDeleteTableRow();
              closePanels();
            }}
            icon={BetweenHorizontalEnd}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-add-col')}
            accessibilityLabel="Add table column"
            disabled={!state.isTableActive}
            onPress={() => {
              actions.onAddTableColumn();
              closePanels();
            }}
            icon={BetweenVerticalStart}
          />
          <IconButton
            testID={tid(testIdPrefix, 'table-del-col')}
            accessibilityLabel="Delete table column"
            disabled={!state.isTableActive}
            onPress={() => {
              actions.onDeleteTableColumn();
              closePanels();
            }}
            icon={BetweenVerticalEnd}
          />
        </View>
      ) : null}

      {openPanel === 'align' ? (
        <View
          style={styles.flyoutRow}
          testID={tid(testIdPrefix, 'align-picker')}
        >
          <IconButton
            testID={tid(testIdPrefix, 'align-left')}
            accessibilityLabel="Align left"
            active={state.activeTextAlign === 'left' || !state.activeTextAlign}
            onPress={() => actions.onAlign('left')}
            icon={AlignLeft}
          />
          <IconButton
            testID={tid(testIdPrefix, 'align-center')}
            accessibilityLabel="Align center"
            active={state.activeTextAlign === 'center'}
            onPress={() => actions.onAlign('center')}
            icon={AlignCenter}
          />
          <IconButton
            testID={tid(testIdPrefix, 'align-right')}
            accessibilityLabel="Align right"
            active={state.activeTextAlign === 'right'}
            onPress={() => actions.onAlign('right')}
            icon={AlignRight}
          />
          {showJustify !== false ? (
            <IconButton
              testID={tid(testIdPrefix, 'align-justify')}
              accessibilityLabel="Justify"
              active={state.activeTextAlign === 'justify'}
              onPress={() => actions.onAlign('justify')}
              icon={AlignJustify}
            />
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

/**
 * Reduced flat one-row toolbar (#1149 Issue 6). Flat individual icons — no
 * grouping into dropdown menus — trimmed to the tools that make sense for a
 * plain-text paste box: undo/redo and clear-formatting do real work; bold /
 * italic / underline / lists / link are cosmetic (the pipeline runs
 * convertToPlainText before send) but kept flat and familiar. Everything that
 * cannot survive plain text — headings, color, image, table, align, strike,
 * code, sub/superscript, indent — is dropped so the row never wraps to two.
 */
function ReducedToolbar({
  testIdPrefix,
  state,
  actions,
  trailing,
  set,
}: TextAreaToolbarProps): React.JSX.Element {
  // The message set has NO undo/redo buttons anywhere (owner, 2026-10-05:
  // "don't need them at all"); the keyboard still undoes. The text-intake
  // modal's reduced row keeps them: this is keyed on the set, not the layout.
  const history =
    set === 'message' ? null : (
      <>
        <IconButton
          testID={tid(testIdPrefix, 'undo')}
          accessibilityLabel="Undo"
          disabled={!state.canUndo}
          onPress={actions.onUndo}
          icon={Undo2}
        />
        <IconButton
          testID={tid(testIdPrefix, 'redo')}
          accessibilityLabel="Redo"
          disabled={!state.canRedo}
          onPress={actions.onRedo}
          icon={Redo2}
        />
        <ToolbarDivider />
      </>
    );
  return (
    <View testID={tid(testIdPrefix, 'toolbar')}>
      <View style={styles.rowReduced}>
        {history}
        <IconButton
          testID={tid(testIdPrefix, 'bold')}
          accessibilityLabel="Bold"
          active={state.isBoldActive}
          onPress={actions.onBold}
          icon={Bold}
        />
        <IconButton
          testID={tid(testIdPrefix, 'italic')}
          accessibilityLabel="Italic"
          active={state.isItalicActive}
          onPress={actions.onItalic}
          icon={Italic}
        />
        <IconButton
          testID={tid(testIdPrefix, 'underline')}
          accessibilityLabel="Underline"
          active={state.isUnderlineActive}
          onPress={actions.onUnderline}
          icon={Underline}
        />
        <ToolbarDivider />
        <IconButton
          testID={tid(testIdPrefix, 'bullet')}
          accessibilityLabel="Bulleted list"
          active={state.isBulletListActive}
          onPress={actions.onToggleBulletList}
          icon={List}
        />
        <IconButton
          testID={tid(testIdPrefix, 'ordered')}
          accessibilityLabel="Numbered list"
          active={state.isOrderedListActive}
          onPress={actions.onToggleOrderedList}
          icon={ListOrdered}
        />
        <ToolbarDivider />
        <IconButton
          testID={tid(testIdPrefix, 'link')}
          accessibilityLabel="Link"
          active={state.isLinkActive}
          disabled={!state.canSetLink && !state.isLinkActive}
          onPress={actions.onLink}
          icon={Link2}
        />
        <IconButton
          testID={tid(testIdPrefix, 'clear-formatting')}
          accessibilityLabel="Clear formatting"
          onPress={actions.onClearFormatting}
          icon={Eraser}
        />
        {trailing}
      </View>
    </View>
  );
}

/**
 * Remembered for the session, PER TOOLBAR (keyed by `testIdPrefix`), so
 * reopening the composer keeps its choice without one toolbar's choice
 * leaking into another's.
 */
const expandableRemembered = new Map<string, boolean>();

function ExpandableToolbar(props: TextAreaToolbarProps): React.JSX.Element {
  const [expanded, setExpanded] = useState(
    expandableRemembered.get(props.testIdPrefix) ?? false,
  );
  const toggle = (
    <IconButton
      testID={tid(props.testIdPrefix, 'more-formatting')}
      accessibilityLabel={
        expanded ? 'Fewer formatting options' : 'More formatting'
      }
      active={expanded}
      onPress={() => {
        // Collapsing unmounts the full row and its local flyouts; the heading
        // picker is the HOST's, so it is closed here or it would still be
        // open, invisibly, when the row comes back.
        if (expanded && props.showHeadingPicker) {
          props.actions.onToggleHeadingPicker();
        }
        expandableRemembered.set(props.testIdPrefix, !expanded);
        setExpanded(!expanded);
      }}
      icon={expanded ? ChevronUp : ChevronDown}
    />
  );
  if (!expanded) {
    return <ReducedToolbar {...props} trailing={toggle} />;
  }
  return (
    <View testID={tid(props.testIdPrefix, 'toolbar')}>
      <FullToolbarRow {...props} trailing={toggle} />
      {props.showHeadingPicker ? (
        <HeadingFlyout
          testIdPrefix={props.testIdPrefix}
          state={props.state}
          actions={props.actions}
        />
      ) : null}
    </View>
  );
}

export function TextAreaToolbar(
  props: TextAreaToolbarProps,
): React.JSX.Element {
  const layout = props.layout ?? (Platform.OS === 'web' ? 'full' : 'compact');

  if (layout === 'compact') {
    return <CompactToolbar {...props} />;
  }

  if (layout === 'reduced') {
    return <ReducedToolbar {...props} />;
  }

  if (layout === 'expandable') {
    return <ExpandableToolbar {...props} />;
  }

  return (
    <View testID={tid(props.testIdPrefix, 'toolbar')}>
      <FullToolbarRow {...props} />
      {props.showHeadingPicker ? (
        <HeadingFlyout
          testIdPrefix={props.testIdPrefix}
          state={props.state}
          actions={props.actions}
        />
      ) : null}
    </View>
  );
}

const styles = StyleSheet.create({
  rowFull: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 0,
    paddingVertical: 0,
    paddingHorizontal: 2,
    backgroundColor: colors.backgroundPrimary,
  },
  // Reduced flat toolbar (#1149 Issue 6): one row, never wraps.
  rowReduced: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: 0,
    paddingVertical: 0,
    paddingHorizontal: 2,
    backgroundColor: colors.backgroundPrimary,
  },
  group: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
  },
  rowCompact: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    alignSelf: 'stretch',
    width: '100%',
    minWidth: 0,
    gap: 0,
    paddingVertical: 0,
    paddingHorizontal: 0,
    backgroundColor: colors.backgroundPrimary,
  },
  flyoutRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 0,
    paddingVertical: 2,
    paddingHorizontal: 4,
    borderTopWidth: 1,
    borderTopColor: colors.borderCard,
    backgroundColor: colors.backgroundSecondary,
  },
  flyoutHeading: {
    backgroundColor: colors.backgroundSecondary,
  },
  flyoutLabel: {
    paddingHorizontal: 8,
    paddingTop: 6,
    paddingBottom: 2,
    fontSize: fontSize.charCount,
    fontWeight: fontWeight.semibold,
    color: colors.textSecondary,
  },
  button: {
    width: 32,
    height: 32,
    borderRadius: 4,
    backgroundColor: 'transparent',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 0,
  },
  buttonChevron: {
    width: 40,
  },
  buttonCompact: {
    width: 36,
    height: 36,
    maxWidth: 36,
  },
  buttonCompactChevron: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: 0,
    minWidth: 28,
    maxWidth: 44,
    height: 36,
  },
  buttonActive: {
    backgroundColor: 'rgba(255, 107, 53, 0.12)',
  },
  buttonDisabled: {
    opacity: 0.45,
  },
  divider: {
    width: 1,
    height: 20,
    marginHorizontal: 1,
    backgroundColor: '#D1D5DB',
  },
  sizeButton: {
    minWidth: 32,
    height: 32,
    borderRadius: 4,
    backgroundColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: 4,
  },
  sizeButtonText: {
    fontSize: fontSize.formLabel,
    fontWeight: fontWeight.semibold,
  },
  swatchButton: {
    width: 36,
    height: 36,
    borderRadius: 4,
    alignItems: 'center',
    justifyContent: 'center',
  },
  swatch: {
    width: 16,
    height: 16,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: colors.textMuted,
  },
});
